/**
 * colorBuckets.js
 *
 * Numeric-range bucket matcher — extracted verbatim from tags.js UPDATE_AVAILABLE_FILTERS_COLORS.
 * Honors ∞/-∞ string sentinels, per-bucket judgMkr operators, and null → N/A bucket.
 *
 * These three exports form a shared contract used by:
 *   T6  — repoints the existing inspection-level color-filter loop in tags.js
 *   T8  — composable that drives Master-level and CML-level coloring
 *   T10 — count aggregation across all three levels
 *   T11 — 3D/360 paint pass
 *
 * Boundary contract (see matrix at bottom of file):
 *   • judgCrr1 / judgCrr2 containing '∞' / '-∞' are resolved to ±Infinity before comparison.
 *   • Operators '<', '>', '<=', '>=' are strict per JS semantics (boundary inclusive for ≤/≥).
 *   • '==' uses loose equality (intentional — matches original code; handles number/string coercion).
 *   • null operator key matches a null value (the N/A bucket path is handled separately first).
 *   • value == null with a non-N/A bucket → false (fast path before operator lookup).
 */

import { CONDITION_RATING } from '@/features/Tags/components/monitoring/cmlColoringConstants';

/**
 * Returns true if `value` falls inside the bucket defined by `option`.
 *
 * @param {number|string|null} value   - The metric value from the tag (e.g. e[targetProperty]).
 * @param {{ judgMkr1: string|null, judgCrr1: any, judgMkr2: string|null, judgCrr2: any }} option
 * @returns {boolean}
 */
export function matchesBucket(value, option) {
    // judgCrr1 is the upper bound — open-top buckets carry the '∞' sentinel here.
    const j1 = option.judgCrr1?.toString().includes('∞') ? Infinity : option.judgCrr1;
    // judgCrr2 is the lower bound — open-bottom buckets carry the '-∞' sentinel here.
    const j2 = option.judgCrr2?.toString().includes('-∞') ? -Infinity : option.judgCrr2;

    const conditionMap = {
        '<':  v => v < j1,
        '>':  v => v > j2,
        '<=': v => v <= j1,
        '>=': v => v >= j2,
        '==': v => v == j1, // string-equality buckets (Coating/Corrosion/Severity); always compares judgCrr1 — judgCrr2 is unused when judgMkr is '=='. Data invariant: judgCrr1 === judgCrr2 for these buckets.
        null: v => v == null,
    };

    if (value == null) return option.judgMkr1 == null && option.judgMkr2 == null; // N/A bucket

    return conditionMap[option.judgMkr1](value) && conditionMap[option.judgMkr2](value);
}

/**
 * Return the first matching bucket (color option) for a numeric value, or null.
 * Consumed by T8 (composable) to resolve a tag's color bucket at Master/CML level.
 *
 * @param {number|string|null} value
 * @param {Array} colorOptions  - Array of option objects with judgMkr/judgCrr fields.
 * @returns {object|null}
 */
export function bucketOf(value, colorOptions) {
    return (colorOptions || []).find(o => matchesBucket(value, o)) || null;
}

/**
 * Status (enum-key) matcher — Status metric buckets key off the condition rating string.
 * Consumed by T8 to resolve a CML record's status bucket.
 *
 * @param {string|null} conditionRating  - e.g. 'Acceptable', 'RepairRequired', or null.
 * @param {Array} statusOptions          - Array of options each with a `key` property.
 * @returns {object|null}
 */
export function statusBucketOf(conditionRating, statusOptions) {
    if (conditionRating == null) return (statusOptions || []).find(o => o.key === CONDITION_RATING.NO_DATA) || null;
    return (statusOptions || []).find(o => o.key === conditionRating) || null;
}

/*
 * ─── Boundary-Value Contract Matrix ────────────────────────────────────────────
 *
 * 1. Normal closed bucket  { judgMkr1:'<', judgCrr1:0.05, judgMkr2:'>=', judgCrr2:0 }
 *    value  0.00  → true  (lower bound inclusive, >= 0)
 *    value  0.05  → false (upper bound exclusive, < 0.05)
 *    value  0.049 → true
 *    value -0.01  → false (< 0 fails >=)
 *
 * 2. Open-top bucket  { judgMkr1:'<', judgCrr1:'∞', judgMkr2:'>', judgCrr2:0.8 }
 *    j1 = Infinity  → v < Infinity always true for finite numbers
 *    value  5.0  → true
 *    value  0.80 → false (exclusive >)
 *    value  0.81 → true
 *
 * 3. Open-bottom bucket  { judgMkr1:'<=', judgCrr1:0, judgMkr2:'>', judgCrr2:'-∞' }
 *    j2 = -Infinity  → v > -Infinity always true for finite numbers
 *    value  0.0  → true  (<= 0 and > -Infinity)
 *    value -999  → true
 *    value  0.01 → false (<= 0 fails)
 *
 * 4. N/A bucket  { judgMkr1:null, judgCrr1:null, judgMkr2:null, judgCrr2:null }
 *    value null   → true  (both markers are null → N/A bucket)
 *    value 0      → false (non-null value never matches N/A bucket)
 *
 * 5. null value with numeric bucket  { judgMkr1:'<', judgCrr1:0.05, judgMkr2:'>=', judgCrr2:0 }
 *    value null  → false (fast-path: null with non-N/A bucket is false)
 *
 * 6. bucketOf — returns first matching option or null.
 * 7. statusBucketOf — null rating → NoData option; string rating → key match or null.
 * ───────────────────────────────────────────────────────────────────────────────
 */
